import clsx from 'clsx';
import { Link } from 'react-router-dom';
import type { CustomerMatch, LeadInfo } from '../../stores/inbox-store';
import { useInboxDrawerStore } from '../../stores/inbox-drawer-store';

interface KlantinfoRailProps {
  customer: CustomerMatch;
  activeId?: string;
}

function pillColorForLead(status: string): string {
  const s = status.toLowerCase();
  if (s.includes('akkoord') || s.includes('opdracht')) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (s.includes('niet ge') || s.includes('niet geïnter')) return 'bg-slate-100 text-slate-500 border-slate-200';
  if (s.includes('hold')) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-blue-100 text-blue-700 border-blue-200';
}

function pillColorForOrder(status: string): string {
  const s = status.toLowerCase();
  if (s.includes('afgerond')) return 'bg-slate-100 text-slate-500 border-slate-200';
  if (s.includes('geannuleerd')) return 'bg-red-100 text-red-600 border-red-200';
  if (s.includes('klaar') || s.includes('afleveren')) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (s.includes('wasserij')) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-blue-100 text-blue-700 border-blue-200';
}

function orderSuffix(reference: string): string {
  const digits = reference.replace(/\D/g, '');
  if (digits.length >= 2) return digits.slice(-2);
  return reference.slice(-2).toUpperCase();
}

function avatarInitials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p[0]?.toUpperCase() ?? '').join('') || '?';
}

function LeadPill({ lead, isActive, onClick }: { lead: LeadInfo; isActive: boolean; onClick: () => void }) {
  const tooltip = `Lead ${lead.reference} — ${lead.status}${lead.total > 0 ? ` — €${lead.total.toFixed(2)}` : ''}`;
  return (
    <button
      type="button"
      onClick={onClick}
      title={tooltip}
      aria-label={tooltip}
      className={clsx(
        'w-10 h-10 rounded-full border flex items-center justify-center text-[11px] font-semibold transition-all',
        pillColorForLead(lead.status),
        isActive && 'ring-2 ring-indigo-500 ring-offset-2 ring-offset-slate-50'
      )}
    >
      L
    </button>
  );
}

function OrderPill({
  order,
  isActive,
  onClick,
}: {
  order: CustomerMatch['orders'][number];
  isActive: boolean;
  onClick: () => void;
}) {
  const tooltip = `${order.reference} — ${order.status}${order.total > 0 ? ` — €${order.total.toFixed(2)}` : ''}`;
  return (
    <button
      type="button"
      onClick={onClick}
      title={tooltip}
      aria-label={tooltip}
      className={clsx(
        'w-10 h-10 rounded-full border flex items-center justify-center text-[11px] font-semibold transition-all',
        pillColorForOrder(order.status),
        isActive && 'ring-2 ring-indigo-500 ring-offset-2 ring-offset-slate-50'
      )}
    >
      {orderSuffix(order.reference)}
    </button>
  );
}

export function KlantinfoRail({ customer, activeId }: KlantinfoRailProps) {
  const open = useInboxDrawerStore((s) => s.open);
  const leads = customer.leads ?? [];
  const orders = customer.orders;
  const name = customer.customer?.name;
  const contact = customer.customer?.contact;
  const customerId = customer.customer?.id;
  const customerType = customer.customer?.type;

  return (
    <div className="w-16 flex-shrink-0 flex flex-col items-center gap-2 py-3 border-l border-r border-slate-200 bg-slate-50 overflow-y-auto">
      {customerId ? (
        <Link
          to={`/klanten/${customerId}?type=${customerType}`}
          className="relative w-10 h-10 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-xs font-semibold hover:bg-slate-300 transition-colors"
          title={contact ? `${name} — ${contact.name}` : (name || 'Klant')}
        >
          {avatarInitials(name)}
          {contact && (
            <span
              aria-hidden
              className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-white border border-slate-300 text-[9px] flex items-center justify-center"
              title={contact.name}
            >
              👤
            </span>
          )}
        </Link>
      ) : (
        <div
          className="w-10 h-10 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-xs font-semibold"
          title={name || 'Klant'}
        >
          {avatarInitials(name)}
        </div>
      )}

      {(leads.length > 0 || orders.length > 0) && (
        <div className="w-6 border-t border-slate-200" />
      )}

      {leads.map((lead) => (
        <LeadPill
          key={lead.id}
          lead={lead}
          isActive={activeId === lead.id}
          onClick={() => open('lead', lead.id)}
        />
      ))}

      {orders.map((order) => (
        <OrderPill
          key={order.id}
          order={order}
          isActive={activeId === order.id}
          onClick={() => open('order', order.id)}
        />
      ))}
    </div>
  );
}
